/*
  Окно «Ряд месяцев» (23.10.2026, решение владельца по макету 6А «сделать все три»): одна
  услуга, одна сумма за каждый месяц с первого по последний - одной операцией.

  Сервер пишет ряд целиком или не пишет ничего: месяц, в котором эта услуга уже есть или
  который вошёл в поданные документы, - отказ с перечнем таких месяцев (показывается в
  окне). Срок оплаты - число следующего месяца; пусто - из настроек организации, как у
  загруженного файла.
*/

import { useState } from 'react'

import { api } from '../../api/client.js'
import { lastMonths, plural } from '../../api/format.js'
import { Field } from '../Common.jsx'
import Dropdown from '../Select.jsx'
import FormDialog from './FormDialog.jsx'
import { asDecimal, isMoney } from './money.js'

const MONTHS = lastMonths(120)
const DUE_DAY = /^([1-9]|[12]\d|3[01])$/

const DUE_DAY_HINT =
  'Число следующего месяца, до которого платят за месяц: «10» - за август до 10 сентября. ' +
  'Оставьте пустым - срок возьмётся из настроек организации.'

/* Сколько месяцев в ряду: ключи вида «2026-08», считается без дат и часовых поясов. */
function monthsBetween(first, last) {
  if (!first || !last) return 0
  const [y1, m1] = first.split('-').map(Number)
  const [y2, m2] = last.split('-').map(Number)
  return (y2 - y1) * 12 + (m2 - m1) + 1
}

export default function ChargeSeriesDialog({ accountId, onSaved, onClose }) {
  const [form, setForm] = useState({ service: '', first: '', last: '', amount: '', dueDay: '' })
  const set = (name) => (event) => setForm({ ...form, [name]: event.target.value })
  const count = monthsBetween(form.first, form.last)

  const valid =
    form.service.trim() !== '' &&
    count > 0 &&
    isMoney(form.amount) &&
    (form.dueDay === '' || DUE_DAY.test(form.dueDay))

  function submit() {
    return api.addChargeSeries(accountId, {
      service: form.service.trim(),
      first_month: `${form.first}-01`,
      last_month: `${form.last}-01`,
      amount: asDecimal(form.amount),
      due_day: form.dueDay === '' ? null : Number(form.dueDay),
    })
  }

  return (
    <FormDialog
      title="Ряд месяцев"
      note="Одна услуга, одна сумма за каждый месяц периода"
      submitLabel={count > 0 ? `Внести ${plural(count, 'месяц', 'месяца', 'месяцев')}` : 'Внести ряд'}
      canSubmit={valid}
      onSubmit={submit}
      onSaved={onSaved}
      onClose={onClose}
    >
      <div className="row">
        <Field label="Услуга" width="m">
          <input
            className="field__input"
            value={form.service}
            maxLength={64}
            onChange={set('service')}
            placeholder="например, отопление"
          />
        </Field>
      </div>
      <div className="row">
        <div className="field field--s">
          <span className="field__label" id="series-first-label">
            Первый месяц
          </span>
          <Dropdown
            value={form.first}
            onChange={(first) => setForm({ ...form, first })}
            options={MONTHS}
            empty="выберите"
            labelledBy="series-first-label"
          />
        </div>
        <div className="field field--s">
          <span className="field__label" id="series-last-label">
            Последний месяц
          </span>
          <Dropdown
            value={form.last}
            onChange={(last) => setForm({ ...form, last })}
            options={MONTHS}
            empty="выберите"
            labelledBy="series-last-label"
          />
        </div>
      </div>
      <div className="row">
        <Field label="Начислено за месяц, ₽" width="s">
          <input
            className="field__input"
            inputMode="decimal"
            value={form.amount}
            onChange={set('amount')}
            placeholder="0,00"
          />
        </Field>
        <Field label="Срок оплаты, число" width="s" hint={DUE_DAY_HINT}>
          <input
            className="field__input"
            inputMode="numeric"
            value={form.dueDay}
            onChange={set('dueDay')}
            placeholder="из настроек"
          />
        </Field>
      </div>
      {form.first && form.last && count <= 0 ? (
        <p className="muted">Первый месяц позже последнего - поменяйте их местами.</p>
      ) : null}
    </FormDialog>
  )
}
